\
<template>
  <div class="-assets-box js-assets-box" v-if="isStoryAttached">
    <div class="wt-lightbox timeline">
      <div class="wt-full-lightbox">
        <div v-if="showTitles" class="-title" ref="visualSectionTitle">
          <span class="wticon wticon-video-clapper"></span>
          <span>Visual</span>
        </div>
        <div class="-visual-assets tooltip-container" ref="visualSection">
          <wt-lightbox-asset
            v-show="showVisualAssets"
            v-for="asset in lightboxUploadingAssets"
            :key="asset.metadata.assetUUID"
            class="timeline"
            :asset="asset"
            :uploading="true"
            @remove-asset="ABORT_LIGHTBOX_UPLOAD_ASSET(asset)"
            @edit="editAsset"
            @upload-done="LIGHTBOX_UPLOAD_DONE(asset)"
            @locate="locateAssetOnTimeline"
          />

          <tm-movable-visual
            v-for="asset in filteredSelectedAssets"
            :key="asset.id"
            :asset="asset"
          >
            <wt-lightbox-asset
              class="timeline"
              :assetUUID="asset.metadata.assetUUID"
              :startTime="asset.startTime"
              :endTime="asset.endTime"
              :asset="asset"
              @preview-asset="previewLightboxAsset"
              @edit="editAsset"
              @add="addVisualAsset(asset)"
              @locate="locateAssetOnTimeline"
              ref="visual-asset"
            />
          </tm-movable-visual>
        </div>
        <div v-if="showTitles" class="-title">
          <span class="wticon wticon-mic"></span>
          <span>Audio</span>
        </div>
        <div class="-audio-assets" ref="audioSection">
          <wt-lightbox-asset
            v-show="showAudioAssets"
            v-for="asset in lightboxUploadingVoiceOverAssets"
            :key="asset.metadata.assetUUID"
            class="timeline"
            :asset="asset"
            :is-narration="true"
            :uploading="true"
            @remove-asset="ABORT_LIGHTBOX_UPLOAD_VOICE_OVER_ASSET(asset)"
            @upload-done="LIGHTBOX_VOICEOVER_UPLOAD_DONE(asset)"
            @locate="locateAssetOnTimeline"
          />

          <tm-movable-audio
            v-for="segment in visibleSegments"
            :key="segment.id"
            :audio="segment.audioSegmentContent"
            v-if="showAudioAssets"
          >
            <wt-audio-asset
              class="timeline"
              :key="segment.guid"
              :segment="segment"
              @add="addAudioAsset(segment)"
              @remove="removeAudioSegment(segment)"
              :assetUUID="segment.audioSegmentContent.asset.metadata.assetUUID"
              ref="audio-asset"
              @edit="editAsset(segment.audioSegmentContent.asset)"
              @locate="locateAssetOnTimeline"
            />
          </tm-movable-audio>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import app from 'app';
import { mapState, mapGetters, mapActions } from 'vuex';
import tmMovableVisual from 'v/Story/Timeline/view/movable/tmMovableVisual.vue';
import tmMovableAudio from 'v/Story/Timeline/view/movable/tmMovableAudio.vue';
import wtAudioAsset from 'components/assets/lightbox/wtAudioAsset.component.vue';
import storyViewBus from 'views/Story/storyViewBus.js';
import LightboxAutoscrollMixin from './LightboxAutoscrollMixin';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';

export default {
  name: 'assets-box',
  mixins: [LightboxAutoscrollMixin],

  inject: ['gotoWallpaper'],

  data() {
    return {
      filterQuery: '',
      skipLightboxHighlight: false,
    };
  },

  components: {
    tmMovableVisual,
    tmMovableAudio,
    wtAudioAsset,
  },

  mounted() {
    storyViewBus.$on('lightboxAssetPreviewClosed', this.closedPreviewAsset);
  },

  beforeDestroy() {
    storyViewBus.$off('lightboxAssetPreviewClosed', this.closedPreviewAsset);
  },

  watch: {
    selectedAssetsSearchQuery() {
      this.setSearchQuery();
    },
    selectedTimelineTrack(newTrack, oldTrack) {
      if (newTrack === oldTrack || this.skipLightboxHighlight) {
        return;
      }
      this.resetLbElementsHighlights();
      if (!newTrack || !this.selectedTimelineTrack) {
        return;
      }
      const isAudio = app.assetType.isAUDIO(this.selectedTimelineTrack.data);
      const assetUUID = this.selectedTimelineTrack.data?.metadata?.assetUUID;
      const startTime = this.selectedTimelineTrack.data?.startTime;
      const endTime = this.selectedTimelineTrack.data?.endTime;
      this.highlightOnLightbox(assetUUID, isAudio, startTime, endTime);
    },
  },

  computed: {
    selectedTimelineTrack() {
      return this.selectedVisualTrack || this.selectedAudioTrack;
    },
    filteredSelectedAssets() {
      let selectedAssets = this.selectedAssets || [];
      const query = this.filterQuery?.toLowerCase().trim();
      if (query) {
        selectedAssets = selectedAssets.filter((asset) => {
          return (
            asset.metadata?.title?.toLowerCase().includes(query) ||
            asset.metadata?.description?.toLowerCase().includes(query) ||
            (asset.metadata?.keywords &&
              asset.metadata.keywords.some((keyword) =>
                keyword.toLowerCase().includes(query)
              )) ||
            asset.metadata?.caption?.toLowerCase().includes(query)
          );
        });
      }
      if (!this.selectedAssetsFilter) {
        return selectedAssets;
      }
      return selectedAssets.filter((asset) => {
        if (this.selectedAssetsFilter === 'IMAGE') {
          return (
            app.assetType.isIMAGE(asset) ||
            app.assetType.isSOCIALIMAGE(asset) ||
            app.assetType.isWALLPAPER(asset) ||
            app.assetType.isWEBPAGE(asset)
          );
        } else if (this.selectedAssetsFilter === 'VIDEO') {
          return (
            app.assetType.isVIDEO(asset) ||
            app.assetType.isGIF(asset) ||
            app.assetType.isFRAGMENT(asset) ||
            app.assetType.isSOCIALVIDEO(asset) ||
            app.assetType.isSOCIALFRAGMENT(asset)
          );
        } else if (this.selectedAssetsFilter === 'UPLOADS') {
          return asset.metadata?.uploadSource;
        }
      });
    },
    mediaCount() {
      let count = 0;
      count += this.filteredSelectedAssets?.length || 0;
      count += this.lightboxUploadingAssets?.length || 0;
      return count;
    },
    audioCount() {
      let count = 0;
      let segments = this.visibleSegments || [];
      segments = _.filter(segments, (segment) => !segment._deleted);
      count += segments.length;
      count += this.lightfboxUploadingVoiceOverAssets?.length || 0;
      return count;
    },
    showTitles() {
      return (
        (this.mediaCount || this.lightboxUploadingAssets.length) &&
        this.showVisualAssets &&
        (this.audioCount || this.lightboxUploadingVoiceOverAssets?.length) &&
        this.showAudioAssets
      );
    },
    showVisualAssets() {
      return (
        !this.selectedAssetsFilter || this.selectedAssetsFilter !== 'AUDIO'
      );
    },
    showAudioAssets() {
      return (
        !this.selectedAssetsFilter ||
        this.selectedAssetsFilter === 'AUDIO' ||
        this.selectedAssetsFilter === 'UPLOADS'
      );
    },
    visibleSegments() {
      const query = this.filterQuery?.toLowerCase().trim();

      return this.selectedVoiceOverSegments.filter((segment) => {
        const isDeleted = segment._deleted;
        const metadata = segment.audioSegmentContent?.asset?.metadata || {};
        return (
          !isDeleted &&
          (!query ||
            (query && metadata.title?.toLowerCase().includes(query)) ||
            (query && metadata.caption?.toLowerCase().includes(query)) ||
            (query && metadata.transcript?.toLowerCase().includes(query)))
        );
      });
    },
    ...mapState({
      lightboxUploadingAssets: (state) => state.story.lightboxUploadingAssets,
      lightboxUploadingVoiceOverAssets: (state) =>
        state.story.lightboxUploadingVoiceOverAssets,
      isStoryAttached: (state) => !!state.story,
      selectedAssetsFilter: (state) => state.story.selectedAssetsFilter,
      selectedAssetsSearchQuery: (state) =>
        state.story.selectedAssetsSearchQuery,
      selectedVisualTrack: (state) => state.timemachine.selected.visual,
      selectedAudioTrack: (state) => state.timemachine.selected.audio,
    }),
    ...mapState('timemachine', {
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      SECONDHAND: (state) => state.secondhand,
      visualTrackLayer: (state) => state.trackLayer.visual,
      audioTracksLayer: (state) => state.trackLayer.audio,
    }),
    ...mapGetters(['selectedAssets', 'selectedVoiceOverSegments']),
  },

  methods: {
    ...mapActions([
      'ABORT_LIGHTBOX_UPLOAD_ASSET',
      'ABORT_LIGHTBOX_UPLOAD_VOICE_OVER_ASSET',
      'LIGHTBOX_UPLOAD_DONE',
      'VOICEOVER_SEGMENT_UNSELECTED',
      'LIGHTBOX_VOICEOVER_UPLOAD_DONE',
    ]),
    ...mapActions('timemachine', [
      'SET_SECONDHAND',
      'SELECT_VISUAL',
      'SELECT_AUDIO',
    ]),

    closedPreviewAsset({ asset }) {
      this.resetLbElementsHighlights();

      if (!asset) {
        return;
      }

      const assetUUID = asset.metadata?.assetUUID;
      const isAudio = app.assetType.isAUDIO(asset);
      const startTime = asset.startTime;
      const endTime = asset.endTime;

      const $lbEl = this.highlightOnLightbox(
        assetUUID,
        isAudio,
        startTime,
        endTime
      );

      this.locateAssetOnTimeline(asset, {
        skipLightboxHighlight: true,
      });
    },

    setSearchQuery: _.debounce(
      function () {
        this.filterQuery = this.selectedAssetsSearchQuery;
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: true }
    ),

    locateAudioSegmentOnTimeline(segment) {
      const assetLocations = [];
      const assetUUID =
        segment.metadata?.assetUUID ||
        segment.audioSegmentContent?.asset?.metadata?.assetUUID;

      if (!assetUUID) {
        return;
      }

      for (let i = 0; i < this.audioTracksLayer.length; i++) {
        const track = this.audioTracksLayer[i];
        const assetUuidMatch = track.data.metadata.assetUUID === assetUUID;

        if (assetUuidMatch) {
          assetLocations.push(track.data.displayStartTime);
        }
      }

      if (!assetLocations.length) {
        return;
      }
      const nextTime = assetLocations.find(
        (location) => location > this.SECONDHAND
      );
      const time = nextTime || assetLocations[0];
      this.SET_SECONDHAND({ time, makeItVisible: true });
      this.SELECT_AUDIO({ time });
      storyViewBus.$emit('svbSeekRcp', time, true);
      return true;
    },

    locateVisualAssetOnTimeline(asset) {
      const exactLocations = [];
      const startTimeLocations = [];
      const assetLocations = [];
      const segmentStartTime = asset.startTime;
      const segmentEndTime = asset.endTime;
      const assetUUID = asset.metadata.assetUUID;

      for (let i = 0; i < this.visualTrackLayer.length; i++) {
        const track = this.visualTrackLayer[i];
        const assetUuidMatch = track.data.metadata.assetUUID === assetUUID;

        if (
          assetUuidMatch &&
          track.data.startTime === segmentStartTime &&
          track.data.endTime === segmentEndTime
        ) {
          exactLocations.push(track.data.displayStartTime);
        } else if (
          assetUuidMatch &&
          track.data.startTime === segmentStartTime
        ) {
          startTimeLocations.push(track.data.displayStartTime);
        } else if (assetUuidMatch) {
          assetLocations.push(track.data.displayStartTime);
        } else {
          // if not as scene, maybe its used as overlay or as custom overlay
          const usedAsCustomOverlay =
            PictureInPictureAdapter.hasThisCustomOverlay(track.data, assetUUID);
          const usedAsSceneOverlay = PictureInPictureAdapter.hasThisOverlay(
            track.data,
            assetUUID
          );
          if (usedAsCustomOverlay || usedAsSceneOverlay) {
            assetLocations.push(track.data.displayStartTime);
          }
        }
      }
      const locations = [
        ...exactLocations,
        ...startTimeLocations,
        ...assetLocations,
      ];
      if (!locations.length) {
        return;
      }
      const nextTime = locations.find((location) => location > this.SECONDHAND);
      const time = (nextTime || locations[0]) + 0.001;
      storyViewBus.$emit('svbSeekRcp', time, true);
      this.SET_SECONDHAND({ time, makeItVisible: true });
      this.SELECT_VISUAL({ time });
      return true;
    },

    locateAssetOnTimeline(asset, options) {
      if (options?.skipLightboxHighlight) {
        this.skipLightboxHighlight = true;
      }
      if (asset.audioSegmentContent || app.assetType.isAUDIO(asset)) {
        this.locateAudioSegmentOnTimeline(asset);
      } else {
        this.locateVisualAssetOnTimeline(asset);
      }
      this.$nextTick(() => {
        this.skipLightboxHighlight = false;
      });
    },

    isVisible(ele, container) {
      if (!ele || !container) {
        return false;
      }

      const containerTop = container.offsetTop;
      const containerScrollTop = container.scrollTop;
      const containerBottom = containerTop + container.clientHeight;
      const eleTop = ele.offsetTop;
      const eleBottom = eleTop + ele.clientHeight;

      return (
        eleTop >= containerTop + containerScrollTop &&
        eleBottom <= containerBottom + containerScrollTop
      );
    },

    highlightOnLightbox(assetUUID, isAudio, startTime, endTime) {
      let $assetUuuidEl = null;
      let $assetStartTimeEl = null;
      let $assetEndTimeEl = null;
      let $exactTimeEl = null;
      if (isAudio) {
        const audioEls = this.$refs['audio-asset'];
        audioEls?.forEach((audioEl) => {
          const audioElAssetUUID = audioEl.$el.getAttribute('assetUUID');
          if (audioElAssetUUID === assetUUID) {
            $assetUuuidEl = audioEl;
          }
        });
      } else {
        const visualEls = this.$refs['visual-asset'];
        this.selectedAssets?.forEach((selectedAsset) => {
          let visualUuidMatch = false;
          const selectedAssetUUID = selectedAsset.metadata.assetUUID;
          visualUuidMatch = selectedAssetUUID === assetUUID;

          if (visualUuidMatch) {
            visualEls.forEach((el) => {
              const elAssetUUID = el.$el.getAttribute('assetUUID');
              const elStartTime = el.$el.getAttribute('startTime');
              const elEndTime = el.$el.getAttribute('endTime');
              if (elAssetUUID === assetUUID) {
                if (
                  (startTime || endTime) &&
                  elStartTime == startTime &&
                  elEndTime == endTime
                ) {
                  $exactTimeEl = el;
                } else if ((startTime || endTime) && elStartTime == startTime) {
                  $assetStartTimeEl = el;
                } else if ((startTime || endTime) && elEndTime == endTime) {
                  $assetEndTimeEl = el;
                } else if (!$assetUuuidEl) {
                  $assetUuuidEl = el;
                }

                if ($exactTimeEl) {
                  return;
                }
              }
            });
          }

          if ($exactTimeEl) {
            return;
          }
        });
      }

      const $lbEl =
        $exactTimeEl || $assetStartTimeEl || $assetEndTimeEl || $assetUuuidEl;
      if ($lbEl) {
        // console.log('highlightOnLightbox -> $lbEl', $lbEl);
        if (
          !this.isVisible($lbEl.$el, document.querySelector('.js-assets-box'))
        ) {
          $lbEl.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        if ($lbEl?.$el) {
          $lbEl.$el.classList.add('-highlight');
        }
        return $lbEl;
      } else {
        return null;
      }
    },

    resetLbElementsHighlights: _.debounce(
      function () {
        const visualEls = this.$refs['visual-asset'] || [];
        const audioEls = this.$refs['audio-asset'] || [];
        [...visualEls, ...audioEls].forEach((el) => {
          el.$el.classList.remove('-highlight');
        });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),

    previewLightboxAsset(asset) {
      app.namespace.Controller.AssetController.previewLightboxAsset(
        asset,
        this.filteredSelectedAssets
      );
    },

    editAsset(asset) {
      if (app.assetType.isAUDIO(asset)) {
        this.$emit('edit-audio-asset', asset);
      } else if (app.assetType.isWALLPAPER(asset)) {
        this.gotoWallpaper(asset);
      }
    },
    removeAudioSegment(segment) {
      this.VOICEOVER_SEGMENT_UNSELECTED(segment);
      const { assetUUID } = segment.audioSegmentContent.asset.metadata;
      storyViewBus.$emit('svbRemoveAudioTrack', { assetUUID });
    },
    addAsset(asset, type) {
      const assetClone = _.cloneDeep(asset);
      storyViewBus.$emit('svbAppendToTimeline', {
        asset: assetClone,
        [type]: true,
      });
    },
    addVisualAsset(asset) {
      this.addAsset(asset, 'visual');
    },
    addAudioAsset(asset) {
      this.addAsset(asset, 'audio');
    },
  },
};
</script>

<style lang="scss" scoped>
.-title {
  color: rgba($black, 0.5);
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  .wticon {
    margin-right: 5px;
  }
}

.-visual-assets,
.-audio-assets {
  display: grid;
  padding-bottom: 20px;
  gap: 15px;
  /* Base case for mobile */
  grid-template-columns: repeat(4, 1fr);
}

/* Desktop and up (992px+) */
@media (min-width: 1400px) {
  .-visual-assets,
  .-audio-assets {
    grid-template-columns: repeat(5, 1fr);
  }
}

/* Extra large desktop and up (1400px+) */
@media (min-width: 1600px) {
  .-visual-assets,
  .-audio-assets {
    grid-template-columns: repeat(6, 1fr);
  }
}
@media (min-width: 1800px) {
  .-visual-assets,
  .-audio-assets {
    grid-template-columns: repeat(7, 1fr);
  }
}
/* Extra large desktop and up (2000px+) */
@media (min-width: 2000px) {
  .-visual-assets,
  .-audio-assets {
    grid-template-columns: repeat(8, 1fr);
  }
}
@media (min-width: 2200px) {
  .-visual-assets,
  .-audio-assets {
    grid-template-columns: repeat(9, 1fr);
  }
}
@media (min-width: 2400px) {
  .-visual-assets,
  .-audio-assets {
    grid-template-columns: repeat(10, 1fr);
  }
}

.-assets-box {
  display: flex;
  width: 100%;
  height: calc(100%);
  overflow-y: auto;
  padding: 45px 20px 20px 20px;
}
</style>
